/**
 * Whose name and logo this installation wears.
 *
 * Resolved once, from the hostname, before anyone signs in — the sign-in screen
 * is the first thing a tenant's users see and the last place a competitor's
 * brand should appear. On the platform's own domain the lookup returns nothing
 * and every consumer falls back to the EduExpertAI defaults, which is why
 * `branding` is a partial object rather than a required one.
 *
 * WHAT THIS DOES NOT OWN
 * ----------------------
 * `--primary`. ThemeContext writes it, and rewrites it whenever the user
 * changes a theme setting; a second writer here would mean the last effect to
 * run wins. The brand colour is handed to ThemeContext through
 * services/brandingService instead, so there is still one writer.
 *
 * Document title and favicon ARE owned here, because nothing else touches them
 * and a tab still reading "EduExpertAI" undoes the rest of the work.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import baseURL, { isPlatformHost } from '@/http';
import {
  PLATFORM_BRAND,
  PLATFORM_CONTEXT,
  TenantContext,
  fetchTenantContext,
  hexToHslTriplet,
  setBrandPrimaryHsl,
} from '@/services/brandingService';

interface BrandingState extends TenantContext {
  /** True until the first lookup settles. Consumers render platform defaults meanwhile. */
  loading: boolean;
}

const BrandingCtx = createContext<BrandingState>({
  ...PLATFORM_CONTEXT,
  loading: true,
});

/** The tenant this page belongs to. Safe to call anywhere below the provider. */
export const useBranding = (): BrandingState => useContext(BrandingCtx);

/**
 * The name to show in a shell, with the platform's as the fallback.
 *
 * A helper rather than a value on the context because "which name" differs by
 * surface — a sidebar wants the short one, a sign-in screen the full one — and
 * every caller was otherwise writing the same two `??` chains.
 */
export const useBrandName = (short = false): string => {
  const { branding } = useBranding();
  if (short) return branding.shortName || branding.appName || PLATFORM_BRAND.name;
  return branding.appName || branding.shortName || PLATFORM_BRAND.name;
};

/**
 * The mark to show, and the platform's as a guaranteed fallback.
 *
 * Returns both because a tenant logo is a remote URL that can 404 long after it
 * was saved. Callers render `logoUrl` and swap to `fallbackUrl` on the image's
 * error event, so a broken tenant image degrades to the platform mark rather
 * than to a blank square.
 */
export const useBrandLogo = (): { logoUrl: string; fallbackUrl: string } => {
  const { branding, isPlatform, loading } = useBranding();
  // On an institute's own domain our mark must never appear, not even as the
  // fallback for a broken or missing logo: an empty frame instead.
  if (!isPlatform && !loading) {
    return { logoUrl: branding.logoUrl || BLANK_IMAGE, fallbackUrl: BLANK_IMAGE };
  }
  return {
    logoUrl: branding.logoUrl || PLATFORM_BRAND.logoUrl,
    fallbackUrl: PLATFORM_BRAND.logoUrl,
  };
};

/** A transparent 1×1 GIF: an image that renders as nothing. */
const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const setLinkTag = (rel: string, href: string) => {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
};

/** Replace every page icon with the institute's one. */
const setTenantIcons = (href: string) => {
  document
    .querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="manifest"], meta[name="msapplication-TileImage"]')
    .forEach((node) => {
      const own = node.getAttribute('href');
      if (own === href) return;
      // On their domain nginx serves THEIR manifest at this address; keep it.
      if (node.getAttribute('rel') === 'manifest' && own === '/site.webmanifest' && !isPlatformHost()) return;
      node.remove();
    });
  setLinkTag('icon', href);
  setLinkTag('apple-touch-icon', href);
};

// Also the old "EducExpertAI" spelling some page titles still carry.
const PLATFORM_NAME = /Educ?Expert\s?AI/gi;

/**
 * On an institute's address no tab may say "EduExpertAI". Pages set their own
 * titles (react-helmet, MetaTags) and several still hard-code our name; rather
 * than chase each one, any title that names the platform gets the institute's
 * name instead, whenever it changes. Returns the cleanup.
 */
const keepTitleBranded = (name: string): (() => void) => {
  const fix = () => {
    const title = document.title;
    if (!PLATFORM_NAME.test(title)) return;
    PLATFORM_NAME.lastIndex = 0;
    document.title = title.replace(PLATFORM_NAME, name);
  };
  fix();
  const observer = new MutationObserver(fix);
  observer.observe(document.head, { subtree: true, childList: true, characterData: true });
  return () => observer.disconnect();
};

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<BrandingState>({
    ...PLATFORM_CONTEXT,
    loading: true,
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    fetchTenantContext(baseURL, controller.signal).then((tenant) => {
      if (!active) return;

      // This lookup is a network round-trip, so ThemeContext has long since
      // painted the platform palette by the time it lands. There is one repaint
      // and it is unavoidable without blocking first render on the API.
      //
      // The brand colour wins over the Appearance preset when there is one;
      // ThemeContext reads it through getBrandPrimaryHsl() on every run, so a
      // user changing a theme setting does not silently restore the platform blue.
      // setBrandPrimaryHsl notifies ThemeContext, which repaints the whole
      // palette (lib/brandPalette.ts) — the one writer of the colour vars.
      setBrandPrimaryHsl(hexToHslTriplet(tenant.branding.primaryColor));

      setState({ ...tenant, loading: false });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (state.loading) return;
    const { appName, faviconUrl, logoUrl } = state.branding;
    if (appName) document.title = appName;
    if (state.isPlatform) {
      if (faviconUrl) setLinkTag('icon', faviconUrl);
      return;
    }
    // An institute's address: its favicon, else its logo. Every platform icon
    // goes first — browsers use the FIRST matching <link rel="icon">, and
    // index.html carries six of ours (in dev, and wherever the server did not
    // leave them out), so setting just one would leave EduExpertAI in the tab.
    const icon = faviconUrl || logoUrl;
    if (icon) setTenantIcons(icon);
    // The browser chrome colour (mobile address bar): theirs, or none.
    document.head.querySelectorAll('meta[name="theme-color"], meta[name="msapplication-TileColor"]').forEach((n) => n.remove());
    const primary = state.branding.primaryColor;
    if (primary && /^#[0-9a-f]{6}$/i.test(primary)) {
      const tag = document.createElement('meta');
      tag.name = 'theme-color';
      tag.content = primary;
      document.head.appendChild(tag);
    }
    if (appName) return keepTitleBranded(appName);
  }, [state.loading, state.branding, state.isPlatform]);

  const value = useMemo(() => state, [state]);

  return <BrandingCtx.Provider value={value}>{children}</BrandingCtx.Provider>;
};
